import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, ArrowLeft, Mail, CheckCircle, Server } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email,
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez reessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
            <Server className="text-cyan-400" size={28} />
          </div>
          <div>
            <h1 className="font-bold text-2xl text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ServeurOnline</h1>
            <p className="text-sm text-slate-500">Backup Monitoring SaaS</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800/60 bg-[#0D111A]/70 backdrop-blur-xl p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-sent">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="text-emerald-400" size={32} />
              </div>
              <h2 className="font-heading text-xl font-bold text-white">Email envoye</h2>
              <p className="text-slate-400 text-sm">
                Si un compte existe avec l&apos;adresse <strong className="text-white">{email}</strong>,
                vous recevrez un lien de reinitialisation valable 1 heure.
              </p>
              <p className="text-slate-500 text-xs">
                Si le SMTP n&apos;est pas configure, contactez votre administrateur pour reinitialiser votre mot de passe.
              </p>
              <Link to="/login">
                <Button variant="secondary" className="mt-4 bg-slate-800 hover:bg-slate-700 text-white rounded-xl">
                  <ArrowLeft size={16} className="mr-2" />
                  Retour a la connexion
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-xl font-bold text-white mb-2">
                Mot de passe oublie ?
              </h2>
              <p className="text-slate-400 text-sm mb-6">
                Entrez votre adresse email. Si un compte existe, nous vous enverrons un lien de reinitialisation.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@entreprise.com"
                      required
                      className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500 pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold py-5 rounded-xl"
                  data-testid="forgot-submit-btn"
                >
                  {isLoading ? "Envoi en cours..." : "Envoyer le lien de reinitialisation"}
                </Button>
              </form>
            </>
          )}

          {!sent && (
            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                <ArrowLeft size={14} />
                Retour a la connexion
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
